import {
	Box,
	Button,
	CircularProgress,
	Divider,
	FormControl,
	FormControlLabel,
	InputLabel,
	MenuItem,
	Modal,
	Select,
	Stack,
	Switch,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import apiClient from '../../ApiClient';
import { AppRoutes } from '../../ApplicationRoutes';
import { useScheduleContext } from '../../Contexts/ScheduleContext';
import type { Video } from '../Schedule/SchedulePage';
import CreatorInfoDisplay from './CreatorInfoDisplay';
import Notification from './Notification';
import VideoPlayer from './VideoPlayer';
import { reconstructTitleWithUuid, formatVideoTitles } from '../../utils/video'


dayjs.extend(utc);
dayjs.extend(timezone);

export type CreatorInfoType = {
	duet_disabled: boolean;
	max_video_post_duration_sec: number;
	privacy_level_options: string[];
	stitch_disabled: boolean;
	comment_disabled: boolean;
	creator_avatar_url: string;
	creator_nickname: string;
	creator_username: string;
};

// type CreatorInfoEndpointType = {
// 	data: CreatorInfoType;
// 	error: {
// 		code: string;
// 		message: string;
// 		log_id: string;
// 	};
// };

type NotificationType = {
	open: boolean;
	message: string;
	severity: 'success' | 'error' | 'info' | 'warning';
};

const defaultNotificationState: NotificationType = {
	open: false,
	message: '',
	severity: 'info',
};

const MOCK_CREATOR_INFO: CreatorInfoType = {
	duet_disabled: true,
	max_video_post_duration_sec: 600,
	privacy_level_options: ['FOLLOWER_OF_CREATOR', 'MUTUAL_FOLLOW_FRIENDS', 'SELF_ONLY'],
	stitch_disabled: false,
	comment_disabled: false,
	creator_avatar_url: 'https://image.com',
	creator_nickname: 'hmbakhsh',
	creator_username: 'hmbakhsh',
};

export default function VideoUploadPage() {
	const { selectedTiktokAccount, selectedGroup, postTimes } = useScheduleContext();
	const [videos, setVideos] = useState<Video[]>([]);
	const [openModal, setOpenModal] = useState<boolean>(false);
	const [selectedVideo, setSelectedVideo] = useState<Video | null>(null);
	const [creatorInfo, setCreatorInfo] = useState<CreatorInfoType | null>(MOCK_CREATOR_INFO);
	const [editingVideo, setEditingVideo] = useState<Video | null>(null);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [notification, setNotification] = useState<NotificationType>(defaultNotificationState);
	const navigate = useNavigate();
	const [globalSettings, setGlobalSettings] = useState({
		comment_disabled: true,
		duet_disabled: true,
		stitch_disabled: true,
		disclose_video_content: false,
		your_brand: false,
		branded_content: false,
		globalPrivacyLevel: '',
	});

	// const handleOpenModal = (video: Video) => {
	// 	setSelectedVideo(video);

	// 	console.log(video.privacy_level, video);

	// 	setEditingVideo({ ...video });
	// 	setOpenModal(true);
	// };
	const handleOpenModal = (video: Video) => {
		setSelectedVideo(video);

		// Initialize editingVideo with the video's current settings
		const initialEditingVideo = {
			...video,
			// Only default to "off" if the creator's settings allow it
			comment_disabled: creatorInfo?.comment_disabled ? true : video.comment_disabled,
			duet_disabled: creatorInfo?.duet_disabled ? true : video.duet_disabled,
			stitch_disabled: creatorInfo?.stitch_disabled ? true : video.stitch_disabled,
		};

		setEditingVideo(initialEditingVideo);
		setOpenModal(true);
	};

	const handleCloseModal = () => {
		// Commenting this out to ensure 'Submit Schedule' button is disabled when Commercial Content toggles aren't enabled.
		// setSelectedVideo(null);
		// setEditingVideo(null);
		setOpenModal(false);
	};

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		if (editingVideo) {
			setEditingVideo({
				...editingVideo,
				[e.target.name]: e.target.value,
			});
		}
	};


	const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (editingVideo) {
			const fieldName = e.target.name;
			const newValue = e.target.checked;

			// For comment_disabled, duet_disabled, stitch_disabled, invert the value
			const isInvertedToggle = ['comment_disabled', 'duet_disabled', 'stitch_disabled'].includes(fieldName);
			const actualValue = isInvertedToggle ? !newValue : newValue;

			setEditingVideo({
				...editingVideo,
				[fieldName]: actualValue,
			});
		}
	};

	const handleSelectChange = (e: SelectChangeEvent<string>) => {
		if (editingVideo) {
			setEditingVideo({
				...editingVideo,
				[e.target.name as string]: e.target.value,
			});
		}
	};

	const handleSubmit = async () => {
		setIsSubmitting(true);
		try {
			for (const video of videos) {
				await submitVideoMetadata(video.id);
			}
			await postSchedule();

			setNotification({
				open: true,
				message: 'Schedule submitted successfully!',
				severity: 'success',
			});
		} catch (error) {
			console.error('Error submitting schedule:', error);
			setNotification({
				open: true,
				message: 'Failed to submit schedule. Please try again.',
				severity: 'error',
			});
		} finally {
			setIsSubmitting(false);
		}
	};

	const fetchCreatorInfo = useCallback(async () => {
		try {
			const res = await apiClient.post('auth/tiktok/creator-info/', {
				tiktok_account: selectedTiktokAccount?.id,
			});
			setCreatorInfo(res.data.data);
		} catch (err) {
			console.error('Error fetching creator info');
			setNotification({
				open: true,
				message: 'Failed to fetch creator info',
				severity: 'error',
			});
		}
	}, [selectedTiktokAccount?.id]);

	const fetchVideos = useCallback(async () => {
		try {
			apiClient
				.get('/core/videos/')
				.then(res => res.data as Video[])
				.then(data => data.filter(v => v.group === selectedGroup))
				.then(filteredVideos => {
					const videosWithFormattedTitles = formatVideoTitles(filteredVideos);
					setVideos(videosWithFormattedTitles);
				});
		} catch (err) {
			console.error(err);
		}
	}, [selectedGroup]);

	const createPostTimeObject = () => {
		const result: Record<string, string | null> = {};

		const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

		postTimes.forEach((time, index) => {
			if (time) {
				const timeInUserTz = dayjs(time).tz(userTimezone);
				const timeInUTC = timeInUserTz.utc();

				result[`post_time_${index + 1}`] = timeInUTC.format('HH:mm:ss');

			} else {
				result[`post_time_${index + 1}`] = null;
			}
		});

		return result;
	};

	const postSchedule = async () => {
		const postTimeObject = createPostTimeObject();
		const requestBody = {
			group: selectedGroup,
			tiktok_account: selectedTiktokAccount?.id,
			...postTimeObject,
		};

		try {
			await apiClient.get('/core/recurringschedule/by-group/', {
				params: {
					group: selectedGroup,
					tiktok_account: selectedTiktokAccount?.id,
				},
			});

			await apiClient.put('/core/recurringschedule/update-by-group/', requestBody);
		} catch (err: any) {
			if (err?.response?.status === 404) {
				await apiClient.post('/core/recurringschedule/', requestBody);
			} else {
				console.error('❌ Failed to submit schedule:', {
					status: err?.response?.status,
					message: err?.message,
					data: err?.response?.data,
				});
				throw err;
			}
		}
	};

	const handleUpdateVideo = async () => {
		if (!editingVideo) return;

		try {
			const titleWithUuid = reconstructTitleWithUuid(editingVideo.title || '', editingVideo.uuid || '');
			const { uuid, ...videoForBackend } = editingVideo;
			videoForBackend.title = titleWithUuid;
			const updatedVideos = videos.map(v => (v.id === editingVideo.id ? editingVideo : v));

			setVideos(updatedVideos);

			await submitVideoMetadata(editingVideo.id, videoForBackend);

			setNotification({
				open: true,
				message: 'Video updated successfully!',
				severity: 'success',
			});
			handleCloseModal();
		} catch (error) {
			console.error('Error updating video:', error);
			setNotification({
				open: true,
				message: 'Failed to update video. Please try again.',
				severity: 'error',
			});
		}
	};
	const submitVideoMetadata = async (videoId: number, videoData?: Video) => {
		try {
			const videoToUpdate = videoData || videos.find(v => v.id === videoId);

			if (!videoToUpdate) throw new Error('Video not found');

			const { video, uuid, ...videoDataToSubmit } = videoToUpdate;

			videoDataToSubmit.title = reconstructTitleWithUuid(videoToUpdate.title, videoToUpdate.uuid || '');

			await apiClient.patch(`core/videos/${videoId}/`, videoDataToSubmit);
		} catch (error) {
			console.error(`Error updating video ${videoId}:`, error);
			throw error;
		}
	};

	useEffect(() => {
		if (selectedGroup.length === 0) {
			navigate(AppRoutes.Schedule);
			return;
		}
		fetchCreatorInfo();
		fetchVideos();
	}, [selectedGroup, selectedTiktokAccount, navigate, fetchCreatorInfo, fetchVideos]);

	const handleCloseNotification = () => {
		setNotification({
			...notification,
			open: false,
		});
	};

	const handleGlobalToggleChange = (toggleName: string) => (e: React.ChangeEvent<HTMLInputElement> | SelectChangeEvent<string>) => {
		let updatedGlobalSettings : any;
		if (toggleName === 'globalPrivacyLevel') {
			const chosenPrivacyLevel = (e as SelectChangeEvent<string>).target.value as Video['privacy_level'];

			updatedGlobalSettings = {
				...globalSettings,
				globalPrivacyLevel: chosenPrivacyLevel ?? '',
				branded_content: chosenPrivacyLevel === 'SELF_ONLY' ? false : globalSettings.branded_content,
			};
		} else {
			const inputEvent = e as React.ChangeEvent<HTMLInputElement>;
			const newValue = inputEvent.target.checked;
			const isInvertedToggle = ['comment_disabled', 'duet_disabled', 'stitch_disabled'].includes(toggleName);
			const actualValue = isInvertedToggle ? !newValue : newValue;

			updatedGlobalSettings = {
				...globalSettings,
				[toggleName]: actualValue,
			};
		}

		setGlobalSettings(updatedGlobalSettings);

		// ! We imply that user wants to use global settings if global settings are being changed
		const updatedVideos = videos.map(video => ({
			...video,
			privacy_level: updatedGlobalSettings.globalPrivacyLevel ? (updatedGlobalSettings.globalPrivacyLevel as Video['privacy_level']) : video.privacy_level,
			comment_disabled: updatedGlobalSettings.comment_disabled,
			duet_disabled: updatedGlobalSettings.duet_disabled,
			stitch_disabled: updatedGlobalSettings.stitch_disabled,
			disclose_video_content: updatedGlobalSettings.disclose_video_content,
			your_brand: updatedGlobalSettings.your_brand,
			branded_content: updatedGlobalSettings.branded_content,
		}));
		setVideos(updatedVideos);

	};

	return (
		<Stack direction={'column'} flex={1} divider={<Divider orientation="horizontal" flexItem />}>
			<Stack direction={'row'} justifyContent={'space-between'} paddingX={'20px'} paddingY={'20px'}>
				<Stack direction={'column'}>
					<Typography fontSize={'25px'} fontWeight={'bold'}>
						Upload Videos
					</Typography>
				</Stack>
			</Stack>
			<Stack
				direction={'column'}
				justifyContent={'space-between'}
				paddingX={'20px'}
				paddingY={'20px'}
				gap={5}
			>
				{/* <Typography fontSize={"15px"} fontWeight={"bold"}>
					{selectedTiktokAccount?.username} Creator Info
				</Typography> */}
				<CreatorInfoDisplay creatorInfo={creatorInfo} />

				{/* Global Settings Section */}
				<Box sx={{ mb: 3 }}>
					<Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
						Global Video Settings
					</Typography>
					<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
						Apply these settings to all videos. You can still modify individual videos later.
					</Typography>

					{/* Global Privacy Level Selector */}
					{creatorInfo && creatorInfo.privacy_level_options && (
						<Box sx={{ mb: 2 }}>
							<Typography variant="body1" sx={{ mb: 1 }}>
								Global Privacy Level
							</Typography>
							<FormControl fullWidth>
								<InputLabel id="global-privacy-level-label">Select Privacy Level</InputLabel>
								<Select
									labelId="global-privacy-level-label"
									id="global-privacy-level"
									value={globalSettings.globalPrivacyLevel}
									label="Select Privacy Level"
									onChange={handleGlobalToggleChange('globalPrivacyLevel')}
								>
									{creatorInfo.privacy_level_options.map(option => (
										<MenuItem key={option} value={option}>
											{option}
										</MenuItem>
									))}
								</Select>
							</FormControl>
							<Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
								This will instantly set the same privacy level for all videos below.
							</Typography>
						</Box>
					)}

					<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
						<FormControlLabel
							control={
								<Switch
									checked={!globalSettings.comment_disabled}
									onChange={handleGlobalToggleChange('comment_disabled')}
									disabled={creatorInfo?.comment_disabled}
								/>
							}
							label="Enable Comments"
						/>

						<FormControlLabel
							control={
								<Switch
									checked={!globalSettings.duet_disabled}
									onChange={handleGlobalToggleChange('duet_disabled')}
									disabled={creatorInfo?.duet_disabled}
								/>
							}
							label="Enable Duets"
						/>

						<FormControlLabel
							control={
								<Switch
									checked={!globalSettings.stitch_disabled}
									onChange={handleGlobalToggleChange('stitch_disabled')}
									disabled={creatorInfo?.stitch_disabled}
								/>
							}
							label="Enable Stitches"
						/>

						<FormControlLabel
							control={
								<Switch
									checked={globalSettings.disclose_video_content}
									onChange={handleGlobalToggleChange('disclose_video_content')}
								/>
							}
							label="Disclose Video Content (All Videos)"
						/>

						{globalSettings.disclose_video_content && (
							<Box sx={{ ml: 3, mt: 1 }}>
								<FormControlLabel
									control={
										<Switch
											checked={globalSettings.your_brand}
											onChange={handleGlobalToggleChange('your_brand')}
										/>
									}
									label="Your Brand"
								/>
								<Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 2 }}>
									Promoting yourself or your own business
								</Typography>

								<FormControlLabel
									control={
										<Switch
											checked={globalSettings.branded_content}
											onChange={handleGlobalToggleChange('branded_content')}
											disabled={!globalSettings.globalPrivacyLevel || globalSettings.globalPrivacyLevel === 'SELF_ONLY'}
										/>
									}
									label="Branded Content"
								/>
								{globalSettings.globalPrivacyLevel === 'SELF_ONLY' && (
									<Typography
										variant="caption"
										color="error"
										sx={{
											display: 'block',
											ml: 2,
											mt: 0.5,
										}}
									>
										Branded Content is not allowed for private videos.
									</Typography>
								)}
								<Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 2 }}>
									Promoting another brand or third party
								</Typography>
							</Box>
						)}
					</Box>
				</Box>

				<TableContainer>
					<Table
						sx={{
							minWidth: 650,
						}}
					>
						<TableHead>
							<TableCell>Video Preview</TableCell>
							<TableCell>Video Name</TableCell>
							<TableCell>Edit Video Form</TableCell>
						</TableHead>
						<TableBody>
							{videos?.map(video => (
								<TableRow key={video.id}>
									<TableCell>
										<VideoPlayer videoUrl={video.video} />
									</TableCell>
									<TableCell
										align="left"
										style={{
											verticalAlign: 'top',
										}}
									>
										<Typography fontSize={'15px'} fontWeight={'bold'}>
											{video.title}
										</Typography>
									</TableCell>
									<TableCell
										align="left"
										style={{
											verticalAlign: 'top',
										}}
									>
										<Button
											variant="contained"
											color="primary"
											onClick={() => handleOpenModal(video)}
										>
											Edit Video Form
										</Button>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</TableContainer>
				<Modal open={openModal} onClose={handleCloseModal} aria-labelledby="edit-video-modal">
					<Box
						sx={{
							position: 'absolute',
							top: '50%',
							left: '50%',
							transform: 'translate(-50%, -50%)',
							width: 400,
							bgcolor: 'background.paper',
							boxShadow: 24,
							p: 4,
							maxHeight: '90vh',
							overflowY: 'auto',
						}}
					>
						<h2 id="edit-video-modal">Edit Video: {selectedVideo?.title}</h2>
						{editingVideo && (
							<form
								onSubmit={e => {
									e.preventDefault();
									handleUpdateVideo();
								}}
							>
								<TextField
									fullWidth
									label="Video Title"
									name="title"
									value={editingVideo.title}
									onChange={handleInputChange}
									margin="normal"
								/>
								<TextField
									fullWidth
									label="Description"
									name="description"
									value={editingVideo.description}
									onChange={handleInputChange}
									margin="normal"
									multiline
									rows={4}
								/>
								<FormControl fullWidth margin="normal">
									<InputLabel id="privacy-level-label">Privacy Level</InputLabel>
									<Select
										labelId="privacy-level-label"
										name="privacy_level"
										value={editingVideo?.privacy_level ?? ''}
										label="Privacy Level"
										onChange={handleSelectChange}
									>
										{creatorInfo?.privacy_level_options.map(option =>
											option === 'SELF_ONLY' && editingVideo.branded_content ? (
												<Tooltip
													key={option}
													title="Branded content visibility cannot be set to private."
													placement="bottom"
													arrow
												>
													<span>
														<MenuItem value={option} disabled={true}>
															{option}
														</MenuItem>
													</span>
												</Tooltip>
											) : (
												<MenuItem
													key={option}
													value={option}
													disabled={editingVideo.branded_content && option === 'SELF_ONLY'}
												>
													{option}
												</MenuItem>
											)
										)}
									</Select>
								</FormControl>
								<FormControlLabel
									control={
										<Switch
											checked={!editingVideo.comment_disabled}
											onChange={handleSwitchChange}
											name="comment_disabled"
											disabled={creatorInfo?.comment_disabled}
										/>
									}
									label="Enable Comments"
								/>
								<FormControlLabel
									control={
										<Switch
											checked={!editingVideo.duet_disabled}
											onChange={handleSwitchChange}
											name="duet_disabled"
											disabled={creatorInfo?.duet_disabled}
										/>
									}
									label="Enable Duets"
								/>
								<FormControlLabel
									control={
										<Switch
											checked={!editingVideo.stitch_disabled}
											onChange={handleSwitchChange}
											name="stitch_disabled"
											disabled={creatorInfo?.stitch_disabled}
										/>
									}
									label="Enable Stitches"
								/>
								<Box
									sx={{
										mb: 1,
									}}
								>
									<FormControlLabel
										control={
											<Switch
												checked={editingVideo.disclose_video_content}
												onChange={handleSwitchChange}
												name="disclose_video_content"
											/>
										}
										label="Disclose Video Content"
									/>
									{editingVideo?.your_brand &&
										editingVideo?.branded_content &&
										editingVideo?.disclose_video_content ? (
										// Show 'Paid partnership' label when BOTH your_brand and branded_content are enabled
										<Typography
											variant="caption"
											color="text.primary"
											sx={{
												display: 'block',
												ml: 2,
												mt: 0.5,
												p: 1.5,
												border: '1px solid #ccc',
												borderRadius: '4px',
												backgroundColor: '#f5f5f5',
											}}
										>
											Your photo/video will be labeled as 'Paid partnership'. This cannot be changed
											once it's been posted.
										</Typography>
									) : editingVideo?.your_brand && editingVideo?.disclose_video_content ? (
										// Show 'Promotional content' label when only your_brand is enabled
										<Typography
											variant="caption"
											color="text.primary"
											sx={{
												display: 'block',
												ml: 2,
												mt: 0.5,
												p: 1.5,
												border: '1px solid #ccc',
												borderRadius: '4px',
												backgroundColor: '#f5f5f5',
											}}
										>
											Your photo/video will be labeled as 'Promotional content'. This cannot be
											changed once it's been posted.
										</Typography>
									) : editingVideo?.branded_content && editingVideo?.disclose_video_content ? (
										// Show 'Paid partnership' label when only branded_content is enabled
										<Typography
											variant="caption"
											color="text.primary"
											sx={{
												display: 'block',
												ml: 2,
												mt: 0.5,
												p: 1.5,
												border: '1px solid #ccc',
												borderRadius: '4px',
												backgroundColor: '#f5f5f5',
											}}
										>
											Your photo/video will be labeled as 'Paid partnership'. This cannot be changed
											once it's been posted.
										</Typography>
									) : null}

									<Typography
										variant="caption"
										color="text.secondary"
										sx={{
											display: 'block',
											ml: 2,
											mt: 0.5,
										}}
									>
										Turn on to disclose that this video promotes goods or services in exchange for
										something of value. Your video could promote yourself, a third party of both.
									</Typography>
								</Box>
								{editingVideo.disclose_video_content && (
									<Box
										sx={{
											mb: 3,
										}}
									>
										<FormControlLabel
											control={
												<Switch
													checked={editingVideo.your_brand}
													onChange={handleSwitchChange}
													name="your_brand"
												/>
											}
											label="Your Brand"
										/>
										<Typography
											variant="caption"
											color="text.secondary"
											sx={{
												display: 'block',
												ml: 2,
												mt: 0.5,
											}}
										>
											Your are promoting yourself or your own business. This video will be
											classified as Brand Organic.
										</Typography>
										<FormControlLabel
											control={
												<Switch
													checked={editingVideo.branded_content}
													onChange={handleSwitchChange}
													name="branded_content"
													disabled={editingVideo.privacy_level === 'SELF_ONLY'}
												/>
											}
											label="Branded Content"
										/>
										{/* Show a message if the switch is disabled */}
										{editingVideo.privacy_level === 'SELF_ONLY' && (
											<Typography
												variant="caption"
												color="error"
												sx={{
													display: 'block',
													ml: 2,
													mt: 0.5,
												}}
											>
												Branded Content is not allowed for private videos.
											</Typography>
										)}
										<Typography
											variant="caption"
											color="text.secondary"
											sx={{
												display: 'block',
												ml: 2,
												mt: 0.5,
											}}
										>
											You are promoting another brand or a third party. This video will be
											classified as Branded Content.
										</Typography>
									</Box>
								)}
								<div>
									<Button onClick={handleCloseModal}>Cancel</Button>
									<Button type="submit">Update Video</Button>
								</div>
							</form>
						)}
					</Box>
				</Modal>
				<Tooltip
					title={
						isSubmitting ||
							!(
								editingVideo?.disclose_video_content &&
								!editingVideo?.your_brand &&
								!editingVideo?.branded_content
							)
							? ''
							: 'You need to indicate if your content promotes yourself, a third party, or both.'
					}
					disableHoverListener={
						!(
							isSubmitting ||
							(editingVideo?.disclose_video_content &&
								!editingVideo?.your_brand &&
								!editingVideo?.branded_content)
						)
					}
					placement="bottom"
					arrow
				>
					<span>
						<Typography
							variant="caption"
							color="text.secondary"
							sx={{
								display: 'block',
								mt: 0.5,
							}}
						>
							By posting, you agree to TikTok's{' '}
							{editingVideo?.branded_content && editingVideo?.disclose_video_content ? (
								<>
									<a
										className="text-blue-500"
										href="https://www.tiktok.com/legal/page/global/bc-policy/en"
									>
										Branded Content Policy
									</a>{' '}
									and{' '}
								</>
							) : null}
							<a
								className="text-blue-500"
								href="https://www.tiktok.com/legal/page/global/music-usage-confirmation/en"
							>
								Music Usage Confirmation
							</a>
							.
						</Typography>
						<Button
							variant="contained"
							color="primary"
							onClick={handleSubmit}
							disabled={
								isSubmitting ||
								(editingVideo?.disclose_video_content &&
									!editingVideo?.your_brand &&
									!editingVideo?.branded_content) ||
								(globalSettings.disclose_video_content &&
									!globalSettings.your_brand &&
									!globalSettings.branded_content)
							}
							sx={{
								mt: 1,
								width: '100%',
							}}
						>
							{isSubmitting ? <CircularProgress size={24} /> : 'Submit Schedule'}
						</Button>
					</span>
				</Tooltip>
				<Notification
					open={notification.open}
					message={notification.message}
					severity={notification.severity}
					onClose={handleCloseNotification}
				/>
			</Stack>
		</Stack>
	);
}
